import axios from 'axios'
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { z } from 'zod'
import {
  ApiError,
  AppError,
  AuthError,
  extractApiError,
} from '../common/errors'
import { LeaguestarState } from '../state'

const login = async (data: LoginForm) => {
  try {
    const user = LoginFormSchema.parse(data)

    const response = await axios.post('/api/login', user, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    if (response.status === 202) {
      return LoginResponseTwoFactorAuthenticationSchema.parse(response.data)
    }

    return LoginSuccessResponseSchema.parse(response.data)
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.isClientError()) {
        throw new Error(error.message)
      }
      throw new Error('Something went wrong. Please try again.')
    }
    throw new Error('An unexpected error occurred.')
  }
}

export const useLogin = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: login,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['isAdmin'] })
    },
  })
}

const loginWithToken = async (data: LoginForm) => {
  try {
    const user = LoginFormWithTokenSchema.parse(data)

    await axios.post('/api/login', user, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.isClientError()) {
        throw new Error(error.message)
      }
      throw new Error('Something went wrong. Please try again.')
    }
    throw new Error('An unexpected error occurred.')
  }
}

export const useLoginWithToken = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: loginWithToken,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['isAdmin'] })
    },
  })
}

const logout = async () => {
  try {
    await axios.delete('/api/logout')
  } catch {
    throw new Error('An unexpected error occurred.')
  }
}

export const useLogout = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: logout,
    onSuccess: () => {
      LeaguestarState.update((s) => {
        s.selected_platform_id = null
      })
      localStorage.removeItem('leaguestar_selected_platform')
      queryClient.clear()
    },
  })
}

const LEAGUESTAR_PLACEHOLDER = '<!--LEAGUESTAR-->'

function getInjectedUserData(): unknown | null {
  const meta = document.querySelector<HTMLMetaElement>(
    'meta[name="leaguestar-data"]'
  )
  if (!meta) return null
  const content = meta.getAttribute('content')
  if (!content || content === LEAGUESTAR_PLACEHOLDER) return null
  try {
    // Browser already decoded &quot; → " before JS reads getAttribute('content')
    return JSON.parse(content)
  } catch {
    return null
  }
}

export function initializePlatformFromMeta(): void {
  const injected = getInjectedUserData()
  if (
    injected !== null &&
    typeof injected === 'object' &&
    'platform_id' in injected
  ) {
    const data = injected as { platform_id?: number; user_id?: number }
    if (data.platform_id && !data.user_id) {
      // Partial injection (unauthenticated) — set platform so axios uses the correct one
      LeaguestarState.update((s) => {
        s.selected_platform_id = data.platform_id!
      })
    }
  }

  // Allow ?platform_id=N in the URL to pin the platform on boot. Used by the
  // admin dashboard preview iframe so the inner page inherits the parent's
  // platform-picker selection without an extra round-trip.
  try {
    const params = new URLSearchParams(window.location.search)
    const raw = params.get('platform_id')
    if (raw) {
      const id = parseInt(raw, 10)
      if (!Number.isNaN(id) && id > 0) {
        LeaguestarState.update((s) => {
          s.selected_platform_id = id
        })
      }
    }
  } catch {
    // window may be unavailable in some environments
  }
}

const getUserData = async (location: string) => {
  try {
    const injected = getInjectedUserData()
    if (injected !== null) {
      // Clear after first read so refreshes fall back to /api/me
      document
        .querySelector<HTMLMetaElement>('meta[name="leaguestar-data"]')
        ?.setAttribute('content', LEAGUESTAR_PLACEHOLDER)
      const result = UserSchema.safeParse(injected)
      if (result.success) return result.data
      // Partial data (e.g. unauthenticated platform-only injection) — fall through to /api/me
    }
    const response = await axios.get('/api/me')
    return UserSchema.parse(response.data)
  } catch (error) {
    // The axios interceptor already converts AxiosErrors to AppError subclasses.
    // Avoid double-wrapping: only extract if it's a raw (unextracted) error.
    const appError =
      error instanceof AppError ? error : extractApiError(error, '/api/me')
    if (appError instanceof AuthError && appError.statusCode === 401) {
      const encodedLocation = encodeURIComponent(location)
      window.location.href = `/login?force=true&redirect=${encodedLocation}`
    }
    throw appError
  }
}

export const useUserData = (location: string) => {
  return useSuspenseQuery({
    queryKey: ['user'],
    queryFn: () => getUserData(location),
    staleTime: 1 * 60 * 1000, // 1 minute
  })
}

const requestPasswordReset = async (data: ForgotPasswordForm) => {
  try {
    const validatedData = ForgotPasswordSchema.parse(data)

    await axios.post('/api/request-password-reset', validatedData, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
  } catch (error) {
    if (
      axios.isAxiosError(error) &&
      error.response &&
      error.response.status === 400
    ) {
      throw new Error(error.response.data.message || 'Invalid email address')
    } else {
      throw new Error('An unexpected error occurred.')
    }
  }
}

export const useRequestPasswordReset = () => {
  return useMutation({
    mutationFn: requestPasswordReset,
  })
}

const completePasswordReset = async (data: PasswordResetForm) => {
  try {
    const validatedData = PasswordResetSchema.parse(data)

    await axios.post('/api/complete-password-reset', validatedData, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      if (error.response.status === 400) {
        throw new Error(
          error.response.data.message || 'Invalid or expired token'
        )
      } else if (error.response.status === 422) {
        throw new Error('Password does not meet security requirements')
      }
    }
    throw new Error('An unexpected error occurred.')
  }
}

export const useCompletePasswordReset = () => {
  return useMutation({
    mutationFn: completePasswordReset,
  })
}

const UserPlatformSchema = z.object({
  platform_id: z.number(),
  platform_name: z.string(),
})

export const UserSchema = z.object({
  user_id: z.number(),
  first_name: z.string(),
  last_name: z.string(),
  email: z.string().email(),
  platforms: z.array(UserPlatformSchema),
  photo_url: z.string().nullable(),
  has_incomplete_required_fields: z.boolean(),
})

export type User = z.infer<typeof UserSchema>

export const LoginFormSchema = z
  .object({
    email: z.string().email({ message: 'Invalid email address.' }).default(''),
    password: z.string().min(1, 'Password is required.').default(''),
  })
  .strict()

export const LoginFormWithTokenSchema = LoginFormSchema.extend({
  token: z.string(),
})

export const LoginResponseTwoFactorAuthenticationSchema = z.object({
  code: z.string(),
  message: z.string(),
})

export const LoginSuccessResponseSchema = z.object({
  email: z.string().email(),
  first_name: z.string(),
  last_name: z.string(),
  user_id: z.number(),
})

export const ForgotPasswordSchema = z.object({
  email: z.string().email('Invalid email address.'),
})

export const PasswordResetSchema = z.object({
  token: z.string(),
  password: z.string().min(8, 'Password must be at least 8 characters'),
})

export type LoginForm = z.infer<typeof LoginFormSchema>
export type LoginFormWithToken = z.infer<typeof LoginFormWithTokenSchema>
export type ForgotPasswordForm = z.infer<typeof ForgotPasswordSchema>
export type PasswordResetForm = z.infer<typeof PasswordResetSchema>
export type LoginSuccessResponse = z.infer<typeof LoginSuccessResponseSchema>
export type LoginResponseTwoFactorAuthentication = z.infer<
  typeof LoginResponseTwoFactorAuthenticationSchema
>
